import React, { useState, useEffect } from 'react';
import { FormLabel, NotificationDispatch, TextField, StyledSpinner } from '@tryhackme/thm-ui-components';
import { faFolderArrowUp } from '@fortawesome/pro-regular-svg-icons';
import { faXmarkCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useGetRoomIconPresignedUrlMutation, useUploadIconToS3Mutation } from 'src/features/my-rooms/my-rooms.slice';
import { UseFormRegister, FieldErrors, UseFormSetValue } from 'src/common/form';
import { ClearButton, InputContainer, InputWrapper, StyledBottomAdornment, StyledFileBrowseButton, StyledFileUploadIcon } from '../design-tab.styles';
import { bannerImageValidationError } from '../design-tab.constants';
interface FormFields {
  headerImage: string;
  imageURL: string;
  uploadedFileName: string;
}
interface RoomBannerUploadProps {
  preselectedFile?: string;
  register: UseFormRegister<FormFields>;
  errors: FieldErrors<FormFields>;
  setValue: UseFormSetValue<FormFields>;
}
export const RoomBannerUpload = ({
  preselectedFile = '',
  errors,
  setValue,
  register
}: RoomBannerUploadProps) => {
  const [fileUrl, setFileUrl] = useState(preselectedFile);
  const [error, setError] = useState('');
  const [uploadFile] = useUploadIconToS3Mutation();
  const supportedFormats = new Set(['png']);
  const [getPresignedURL] = useGetRoomIconPresignedUrlMutation();
  const [loading, setLoading] = React.useState(false);
  useEffect(() => {
    if (preselectedFile) {
      setFileUrl(preselectedFile);
    }
  }, [preselectedFile]);
  const validateFileType = (file: File | string) => {
    if (typeof file === 'string') {
      const extension = file.split('.').pop()?.toLowerCase();
      return extension ? supportedFormats.has(extension) : false;
    }
    const extension = file.name.split('.').pop()?.toLowerCase();
    return extension ? supportedFormats.has(extension) : false;
  };
  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    setFileUrl(url);
    if (url && !validateFileType(url)) {
      setError(bannerImageValidationError);
    } else {
      setError('');
      setValue('headerImage', url, {
        shouldDirty: true
      });
    }
  };
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLoading(true);
      const validFormat = validateFileType(file);
      if (validFormat) {
        setFileUrl('');
        setError('');
        try {
          await uploadFileToS3(file);
        } catch {
          NotificationDispatch('error', 'Something went wrong while uploading file. Please try again.');
        }
      } else {
        setError(bannerImageValidationError);
        setFileUrl('');
        e.target.value = '';
      }
      setLoading(false);
    }
  };
  const uploadFileToS3 = async (file: File) => {
    const presignedResponse = await getPresignedURL({
      filename: file.name,
      type: 'room-banners'
    }).unwrap();
    const {
      url
    } = presignedResponse?.data ?? {};
    if (!url) throw new Error('No URL received');
    await uploadFile({
      file,
      url
    }).unwrap();
    const fileType = file.name.split('.').pop();
    if (!fileType) throw new Error('Invalid file type');
    const baseUrl = url.split('?')[0];
    if (!baseUrl) throw new Error('Invalid URL');
    setValue('headerImage', baseUrl, {
      shouldDirty: true
    });
    setFileUrl(baseUrl ?? '');
  };
  const handleFileBrowse = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  const handleClear = () => {
    setFileUrl('');
    setError('');
    setValue('headerImage', '', {
      shouldDirty: true
    });
  };
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  return <div data-sentry-component="RoomBannerUpload" data-sentry-source-file="room-banner-upload.tsx">
      <FormLabel htmlFor="headerImage" data-sentry-element="FormLabel" data-sentry-source-file="room-banner-upload.tsx">Banner</FormLabel>

      <InputWrapper data-sentry-element="InputWrapper" data-sentry-source-file="room-banner-upload.tsx">
        <InputContainer data-sentry-element="InputContainer" data-sentry-source-file="room-banner-upload.tsx">
          <TextField {...register('headerImage')} data-testid="header-image" name="headerImage" id="headerImage" variant="input" type="text" placeholder="Enter URL or choose a file..." value={fileUrl} onChange={handleUrlChange} disabled={loading} rightAdornment={fileUrl ? <ClearButton onClick={handleClear} data-testid="header-image-clear-button">
                  <FontAwesomeIcon icon={faXmarkCircle} />
                </ClearButton> : <span />} bottomAdornment={<StyledBottomAdornment>Supported formats: PNG Max file size is 5MB</StyledBottomAdornment>} errorMessage={error || errors?.headerImage?.message} data-sentry-element="TextField" data-sentry-source-file="room-banner-upload.tsx" />
        </InputContainer>
        <div>
          <input data-testid="banner-file-input" ref={fileInputRef} type="file" hidden onChange={e => {
          handleFileChange(e).catch(() => {
            NotificationDispatch('error', 'File upload failed');
          });
        }} accept=".png" />
          <StyledFileBrowseButton aria-label="header-image" variant="outlined" color="secondary" onClick={handleFileBrowse} disabled={loading} data-sentry-element="StyledFileBrowseButton" data-sentry-source-file="room-banner-upload.tsx">
            {loading ? <div>
                <StyledSpinner color="#151C2B" /> Uploading file
              </div> : <>
                <StyledFileUploadIcon icon={faFolderArrowUp} />
                Browse...
              </>}
          </StyledFileBrowseButton>
        </div>
      </InputWrapper>
    </div>;
};